import toast from "react-hot-toast";
import { useState, useEffect } from "react";
import * as mediaContentApi from "../api/mediaContent";
import GradientBg from "../components/GradietBg";
import OnlineOrderPaymentQrCode from "../components/OnlineOrderPaymentQrCode";
import OnlineOrderFailedIcon from "../../public/onlineOrderFailed.svg?react";
import OnlineOrderSucceedIcon from "../../public/onlineOrderSucceed.svg?react";
import { useSelector } from "react-redux";
import { selectAccessToken } from "../store/auth";
import { useWindowSize } from "../hooks/useWindowSize.jsx";
import { useTranslation } from "../i18n";

const MediaContent = () => {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const [mediaBlobUrl, setMediaBlobUrl] = useState("");
  const [mediaType, setMediaType] = useState("");
  const { width, height } = useWindowSize();
  const [showOrder, setShowOrder] = useState(false);
  /** @type {ReturnType<typeof useState<OnlineOrder | null>>} */
  const [order, setOrder] = useState(null);
  /** @type {ReturnType<typeof useState<EventSource | null>>} */
  const [eventSource, setEventSource] = useState(null);

  useEffect(() => {
    console.log("[MediaContent] Order state changed:", {
      orderId: order?.id,
      status: order?.status,
      showOrder,
    });

    if (order == null) {
      console.log("[MediaContent] No order, hiding display immediately");
      setShowOrder(false);
      return;
    }

    if (order.status < 4) {
      console.log(
        `[MediaContent] Order status ${order.status} is pending, showing QR`,
      );
      setShowOrder(true);
      return;
    }

    if (order.status === 4) {
      console.log(
        "[MediaContent] Order status 4 (PAYEMENT_FINISHED), showing success screen",
      );
      setShowOrder(true);
      return;
    }

    if (order.status === 5) {
      console.log(
        "[MediaContent] Order status 5 (CANCELLED), hiding immediately",
      );
      setShowOrder(false);
      setOrder(null);
      return;
    }
  }, [order]);

  const initEventSource = async () => {
    console.log("[MediaContent] Initializing EventSource connection");

    // 1. Fetch current active order state first (prevents race condition)
    try {
      const activeOrderData =
        await mediaContentApi.fetchActiveOrder(accessToken);
      if (activeOrderData && activeOrderData.order) {
        console.log(
          "[MediaContent] Found existing active order on init:",
          activeOrderData.order.id,
        );
        setOrder(activeOrderData.order);
      } else {
        console.log("[MediaContent] No active order on init");
      }
    } catch (error) {
      console.error(
        "[MediaContent] Failed to fetch initial active order:",
        error,
      );
    }

    // 2. Subscribe to real-time updates
    const { id } = await mediaContentApi.initUpdatesSubscription(accessToken);
    console.log("[MediaContent] Created EventSource subscription:", id);
    const es = await mediaContentApi.subscribeToUpdates(id);

    es.addEventListener("CustomerOnlineOrderUpdate", (event) => {
      const { order: newOrder } = JSON.parse(event.data);
      console.log("[MediaContent] Received CustomerOnlineOrderUpdate:", {
        orderId: newOrder?.id,
        status: newOrder?.status,
      });
      setOrder(newOrder);
    });

    es.addEventListener("CustomerOnlineOrderStatusUpdate", (event) => {
      const { status: newStatus, orderId } = JSON.parse(event.data);
      console.log("[MediaContent] Received CustomerOnlineOrderStatusUpdate:", {
        orderId,
        newStatus,
      });

      // Use functional update to avoid stale closure bug
      setOrder((currentOrder) => {
        if (currentOrder == null) {
          console.warn(
            "[MediaContent] Received status update but no current order, ignoring",
          );
          return currentOrder;
        }
        if (currentOrder.id !== orderId) {
          console.warn("[MediaContent] Order ID mismatch, ignoring update:", {
            currentId: currentOrder.id,
            updateId: orderId,
          });
          return currentOrder;
        }
        console.log(
          `[MediaContent] Updating order ${orderId} status: ${currentOrder.status} → ${newStatus}`,
        );
        return { ...currentOrder, status: newStatus };
      });
    });

    es.addEventListener("CustomerOnlineOrderHide", (event) => {
      const { orderId } = JSON.parse(event.data);
      console.log("[MediaContent] Received CustomerOnlineOrderHide:", {
        orderId,
      });

      setOrder((currentOrder) => {
        if (currentOrder == null) {
          console.warn(
            "[MediaContent] Received hide event but no current order, ignoring",
          );
          return currentOrder;
        }
        if (currentOrder.id !== orderId) {
          console.warn("[MediaContent] Order ID mismatch in hide event:", {
            currentId: currentOrder.id,
            hideId: orderId,
          });
          return currentOrder;
        }
        console.log(
          `[MediaContent] Hiding order ${orderId} as instructed by server`,
        );
        setShowOrder(false);
        return null;
      });
    });

    es.addEventListener("error", (event) => {
      console.error("[MediaContent] EventSource error:", event);
    });

    es.addEventListener("open", () => {
      console.log("[MediaContent] EventSource connection opened");
    });

    return es;
  };

  useEffect(() => {
    if (eventSource != null || accessToken == null) return;

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    const baseDelay = 2000; // 2 seconds

    const connectEventSource = async () => {
      try {
        console.log("[MediaContent] Connecting EventSource...");
        const es = await initEventSource();

        es.addEventListener("error", () => {
          if (es.readyState === EventSource.CLOSED) {
            console.error(
              "[MediaContent] EventSource connection closed, attempting reconnect",
            );

            if (reconnectAttempts < maxReconnectAttempts) {
              reconnectAttempts++;
              const delay = baseDelay * Math.pow(2, reconnectAttempts - 1); // Exponential backoff
              console.log(
                `[MediaContent] Reconnect attempt ${reconnectAttempts}/${maxReconnectAttempts} in ${delay}ms`,
              );

              setTimeout(() => {
                setEventSource(null); // Trigger reconnection
              }, delay);
            } else {
              console.error(
                "[MediaContent] Max reconnect attempts reached, giving up",
              );
              toast.error(t("toast.connectionLost"));
            }
          }
        });

        es.addEventListener("open", () => {
          console.log("[MediaContent] EventSource connected successfully");
          reconnectAttempts = 0; // Reset counter on successful connection
        });

        setEventSource(es);
      } catch (error) {
        console.error(
          "[MediaContent] Failed to initialize EventSource:",
          error,
        );
        toast.error(t("toast.subscriptionFailed"));
      }
    };

    connectEventSource();

    // Cleanup on unmount
    return () => {
      if (eventSource) {
        console.log("[MediaContent] Closing EventSource on unmount");
        eventSource.close();
      }
    };
  }, [accessToken, eventSource]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const { mediaType, mediaBlobUrl } =
          await mediaContentApi.fetchMedia(accessToken);
        setMediaType(mediaType);
        setMediaBlobUrl(mediaBlobUrl);
      } catch (error) {
        console.error("Error fetching media:", error);
      }
    };
    fetchMedia();
  }, []);

  const renderMedia = () => {
    if (mediaType === "image") {
      return (
        <div className="flex justify-center items-center w-screen h-screen overflow-hidden">
          <img
            src={mediaBlobUrl}
            alt="Fetched media"
            className="max-w-full max-h-full"
          />
        </div>
      );
    } else if (mediaType === "video") {
      return (
        <div className="flex justify-center items-center w-screen h-screen overflow-hidden">
          <video
            src={mediaBlobUrl}
            controls
            className="max-w-full max-h-full"
            autoPlay
            loop
            muted
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }
    return null;
  };

  const renderCurrentOrder = () => {
    if (order == null) {
      return null;
    }

    // The QR payload (and its module count / render size) is logged by
    // OnlineOrderPaymentQrCode itself, which is the single source of truth for
    // what the customer is about to scan.

    if (order.status < 4) {
      if (height > width) {
        return (
          <GradientBg className="h-screen w-screen">
            <div className="px-16 lg:py-64 flex flex-col min-h-screen justify-between">
              <div>
                <h1 className="font-semibold text-6xl text-left mb-4 text-center">
                  {t("media.scanQr")}
                </h1>
                <h1 className="font-semibold text-6xl text-left text-center">
                  {t("media.toConfirmOrder")}
                </h1>
              </div>

              <div className="my-12">
                <OnlineOrderPaymentQrCode onlineOrder={order} size="lg" />
              </div>
              <p className="text-center lg:text-5xl">
                {t("media.continueOnDevice")}
              </p>
            </div>
          </GradientBg>
        );
      }

      return (
        <GradientBg className="h-screen w-screen">
          <div className="px-24 flex min-h-screen justify-between items-center">
            <div>
              <h1 className="font-semibold text-6xl text-left mb-4">
                {t("media.scanQr")}
              </h1>
              <h1 className="font-semibold text-6xl text-left">
                {t("media.toConfirmOrder")}
              </h1>
            </div>
            <div>
              <div className="my-12">
                <OnlineOrderPaymentQrCode onlineOrder={order} size="lg" />
              </div>
              <p className="text-center text-4xl">
                {t("media.continueOnDevice")}
              </p>
            </div>
          </div>
        </GradientBg>
      );
    }

    return (
      <GradientBg className="h-screen w-screen">
        <div className="h-full flex flex-col items-center justify-center h-full">
          {order.status == 4 ? (
            <>
              <OnlineOrderSucceedIcon className="h-96 w-auto" />
              <h1 className="mt-10 text-6xl">Оплата прошла успешно</h1>
            </>
          ) : (
            <>
              <OnlineOrderFailedIcon className="h-96 w-auto" />
              <h1 className="mt-10 text-6xl">УПС, что-то пошло не так</h1>
            </>
          )}
        </div>
      </GradientBg>
    );
  };

  return showOrder ? renderCurrentOrder() : renderMedia();
};

export default MediaContent;
